// src/screens/SessionDuration.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TimePicker from "./TimePicker";
import { PrimaryButton } from "../components/Button";
import CreditsBalance from "../components/CreditBalance";
import Navbar from "../components/Navbar";
import { getUserProfile, bookSession, getUserSessions } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { parseTimeFromSlot as parseTimeFromSlotUtil, formatTo24Hour } from "../utils/timeUtils";
import { parseSlotISO } from "../utils/utcTimeUtils";

// Use the utility function for parsing time slots
const parseTimeFromSlot = parseTimeFromSlotUtil;

export default function SessionDuration() {
  const navigate = useNavigate();
  const { state } = useLocation() as any;
  const { trainer, slot, time, day } = state || {}; // slot is ISO format, time is display format
  const { user } = useAuth();

  // Ensure slot is defined for TypeScript
  const slotISO: string | undefined = slot;

  // Helper function to add minutes to a time string
  const addMinutesToTime = (timeStr: string, minutes: number) => {
    const totalMinutes = parseTime(timeStr) + minutes;
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    let hour12 = hours % 12;
    if (hour12 === 0) hour12 = 12;
    const period = hours < 12 ? 'AM' : 'PM';

    return `${hour12}:${mins.toString().padStart(2, '0')} ${period}`;
  };

  // Parse time strings like "6:00 PM" and "8:00 PM"
  const parseTime = (timeStr: string) => {
    if (!timeStr) return 0;
    const [timePart, period] = timeStr.trim().split(' ');
    const [hours, minutes] = timePart.split(':');
    let hour24 = parseInt(hours);
    if (period === 'PM' && hour24 !== 12) hour24 += 12;
    if (period === 'AM' && hour24 === 12) hour24 = 0;
    return hour24 * 60 + parseInt(minutes || '0');
  };

  // Calculate session duration from start and end times
  const calculateDuration = (start: string, end: string) => {
    const startMinutes = parseTime(start);
    const endMinutes = parseTime(end);
    return endMinutes - startMinutes;
  };

  // Helper function to format time
  const format12Hour = (hour: number, min: number) => {
    const h = hour % 12 || 12;
    const period = hour < 12 ? 'AM' : 'PM';
    return `${h}:${min.toString().padStart(2, '0')} ${period}`;
  };

  // Parse slot to get original boundaries and default booking times
  let originalStartTime = "9:00 AM";
  let originalEndTime = "10:00 AM";
  let defaultStart = "9:00 AM";
  let defaultEnd = "10:00 AM"; // Default to 1 hour from start

  if (slotISO) {
    // New format: slot is in ISO format "startISO-endISO"
    const slotTimes = parseSlotISO(slotISO);
    console.log('SessionDuration - Parsed slot ISO:', slotTimes);

    // Convert to local time for display
    const startDate = new Date(slotTimes.start);
    const endDate = new Date(slotTimes.end);
    const startHour = startDate.getHours();
    const startMin = startDate.getMinutes();
    const endHour = endDate.getHours();
    const endMin = endDate.getMinutes();

    console.log('SessionDuration - Date conversion:', {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      startHour,
      startMin,
      endHour,
      endMin
    });

    // Store the full slot boundaries
    originalStartTime = format12Hour(startHour, startMin);
    originalEndTime = format12Hour(endHour, endMin);

    console.log('SessionDuration - Formatted times:', {
      originalStartTime,
      originalEndTime
    });

    // For booking, start with slot start time and add 1 hour (if slot allows)
    defaultStart = originalStartTime;
    const slotDurationMinutes = parseTime(originalEndTime) - parseTime(originalStartTime);
    console.log('SessionDuration - Slot duration:', slotDurationMinutes, 'minutes');

    if (slotDurationMinutes >= 60) {
      // Slot is at least 1 hour, so set end time to 1 hour from start
      defaultEnd = addMinutesToTime(defaultStart, 60);
    } else {
      // Slot is less than 1 hour, use the full slot
      defaultEnd = originalEndTime;
    }
  } else if (time) {
    // Old format: parse from time string
    const timeSlot = parseTimeFromSlot(time) as { start: string; end: string };
    originalStartTime = timeSlot?.start || "9:00 AM";
    originalEndTime = timeSlot?.end || "10:00 AM";

    // For booking, start with slot start time and add 1 hour (if slot allows)
    defaultStart = originalStartTime;
    const slotDurationMinutes = parseTime(originalEndTime) - parseTime(originalStartTime);
    if (slotDurationMinutes >= 60) {
      defaultEnd = addMinutesToTime(defaultStart, 60);
    } else {
      defaultEnd = originalEndTime;
    }
  }

  // Ensure we have valid original times (fallback)
  if (!originalStartTime || parseTime(originalStartTime) === 0) {
    originalStartTime = "9:00 AM";
  }
  if (!originalEndTime || parseTime(originalEndTime) === 0) {
    originalEndTime = "10:00 AM";
  }

  const [startTime, setStartTime] = useState(defaultStart);
  const [endTime, setEndTime] = useState(defaultEnd);
  const [duration, setDuration] = useState<number>(60); // Start with 1 hour

  // Store the original time slot boundaries (full slot)
  const [originalStartTimeState] = useState(originalStartTime);
  const [originalEndTimeState] = useState(originalEndTime);

  console.log('SessionDuration initialized:', {
    defaultStart,
    defaultEnd,
    originalStartTime,
    originalEndTime,
    originalStartMinutes: parseTime(originalStartTime),
    originalEndMinutes: parseTime(originalEndTime),
    slotISO,
    time
  });
  const [notes, setNotes] = useState<string>("");
  const [agreed, setAgreed] = useState<boolean>(true);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerType, setPickerType] = useState<"start" | "end">("start");

  // User credits and booking state
  const [userCredits, setUserCredits] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [isFirstSession, setIsFirstSession] = useState(false);

  // Sync duration when start/end times change (but don't override manual duration changes)
  // This effect is mainly for when times are changed via picker
  useEffect(() => {
    const newDuration = calculateDuration(startTime, endTime);
    // Only update if the calculated duration is significantly different (avoid infinite loops)
    if (Math.abs(newDuration - duration) > 5) {
      setDuration(newDuration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, endTime]);

  // Fetch user credits on component mount
  useEffect(() => {
    const fetchUserCredits = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        const [profileResponse, sessionsResponse] = await Promise.all([
          getUserProfile(user.id),
          getUserSessions(user.id).catch(e => ({ sessions: [] }))
        ]);
        setUserCredits(profileResponse.user.credits || 0);
        console.log('User credits loaded:', profileResponse.user.credits);
        
        const activeSessions = (sessionsResponse?.sessions || []).filter((s: any) => s.status !== 'cancelled');
        setIsFirstSession(activeSessions.length === 0);
        
      } catch (error) {
        console.error('Error fetching user credits:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserCredits();
  }, [user?.id]);

  const openPicker = (type: "start" | "end") => {
    console.log('openPicker called with type:', type);
    setPickerType(type);
    setShowPicker(true);
  };
  const onPickTime = (t: string) => {
    console.log('onPickTime called:', { pickerType, time: t });
    
    // Parse times for comparison
    const originalStartMinutes = parseTime(originalStartTimeState);
    const originalEndMinutes = parseTime(originalEndTimeState);
    const newPickedMinutes = parseTime(t);
    const currentStartMinutes = parseTime(startTime);
    const currentEndMinutes = parseTime(endTime);

    if (pickerType === "start") {
      // User is changing the START time
      setStartTime(t);
      
      // If new start time >= current end time, force end time to jump to +1 hour
      if (newPickedMinutes >= currentEndMinutes) {
        const newEndTime = addMinutesToTime(t, 60);
        setEndTime(newEndTime);
        setDuration(60);
        console.log(`Auto-jump: Start=${t}, End=${newEndTime} (1hr)`);
      } else {
        // Just update duration based on the new valid range
        const newDuration = calculateDuration(t, endTime);
        setDuration(newDuration);
      }
    } else {
      // User is changing the END time
      // Check if end time is before or equal to current start time
      if (newPickedMinutes <= currentStartMinutes) {
        // Auto-jump end time to be exactly 1 hour after current start time
        const newEndTime = addMinutesToTime(startTime, 60);
        setEndTime(newEndTime);
        setDuration(60);
        console.log(`Auto-jump End Time back to +1hr from start: ${newEndTime}`);
      } else {
        // End time is valid
        setEndTime(t);
        const newDuration = calculateDuration(startTime, t);
        setDuration(newDuration);
      }
    }
    
    setShowPicker(false);
  };

  const dec = () => {
    console.log('dec called, current duration:', duration, 'startTime:', startTime, 'endTime:', endTime);
    console.log('dec - originalEndTimeState:', originalEndTimeState, 'originalStartTimeState:', originalStartTimeState);

    const newDuration = Math.max(60, duration - 60); // Minimum 1 hour, decrement by 1 hour
    const newEndTime = addMinutesToTime(startTime, newDuration);

    // Check if new end time is within original boundaries
    const originalEndMinutes = parseTime(originalEndTimeState);
    const originalStartMinutes = parseTime(originalStartTimeState);
    const newEndMinutes = parseTime(newEndTime);
    const startTimeMinutes = parseTime(startTime);

    console.log('dec validation:', {
      newEndMinutes,
      originalEndMinutes,
      originalStartMinutes,
      newDuration,
      newEndTime,
      startTimeMinutes,
      currentEndMinutes: parseTime(endTime),
      originalEndTimeState,
      originalStartTimeState
    });

    // Validate: end time must be after start time and within original slot
    // If originalEndMinutes is 0 or invalid, skip validation (shouldn't happen, but safety check)
    if (originalEndMinutes > 0 && newEndMinutes > startTimeMinutes && newEndMinutes <= originalEndMinutes && newEndMinutes > originalStartMinutes) {
      setDuration(newDuration);
      setEndTime(newEndTime);
      console.log('dec: updated duration to', newDuration, 'endTime to', newEndTime);
    } else {
      console.log('dec: validation failed - newEndMinutes:', newEndMinutes, 'originalEndMinutes:', originalEndMinutes, 'startTimeMinutes:', startTimeMinutes, 'originalStartMinutes:', originalStartMinutes);
    }
  };

  const inc = () => {
    console.log('inc called, current duration:', duration, 'startTime:', startTime, 'endTime:', endTime);
    console.log('inc - originalEndTimeState:', originalEndTimeState, 'originalStartTimeState:', originalStartTimeState);

    const newDuration = duration + 60; // Increment by 1 hour
    const newEndTime = addMinutesToTime(startTime, newDuration);

    // Check if new end time is within original boundaries
    const originalEndMinutes = parseTime(originalEndTimeState);
    const originalStartMinutes = parseTime(originalStartTimeState);
    const newEndMinutes = parseTime(newEndTime);
    const startTimeMinutes = parseTime(startTime);

    console.log('inc validation:', {
      newEndMinutes,
      originalEndMinutes,
      originalStartMinutes,
      newDuration,
      newEndTime,
      startTimeMinutes,
      currentEndMinutes: parseTime(endTime),
      originalEndTimeState,
      originalStartTimeState
    });

    // Validate: end time must be after start time and within original slot
    // If originalEndMinutes is 0 or invalid, skip validation (shouldn't happen, but safety check)
    if (originalEndMinutes > 0 && newEndMinutes > startTimeMinutes && newEndMinutes <= originalEndMinutes && newEndMinutes > originalStartMinutes) {
      setDuration(newDuration);
      setEndTime(newEndTime);
      console.log('inc: updated duration to', newDuration, 'endTime to', newEndTime);
    } else {
      console.log('inc: validation failed - newEndMinutes:', newEndMinutes, 'originalEndMinutes:', originalEndMinutes, 'startTimeMinutes:', startTimeMinutes, 'originalStartMinutes:', originalStartMinutes);
    }
  };

  const bookNow = async () => {
    if (!agreed) return alert("You must agree to the terms and policies");

    // Calculate required credits using trainer's creditsPerHour
    const hours = duration / 60; // Convert minutes to hours
    const rate = trainer?.creditsPerHour || (trainer?.isGym ? 5 : 1);
    const requiredCredits = Math.ceil(hours * rate);

    if (userCredits >= requiredCredits) {
      // User has enough credits, book directly
      await bookSessionDirectly();
    } else {
      // User needs more credits, go to BuyCredits
      navigate("/student/buy-credits", {
        state: {
          trainer,
          time,
          day,
          startTime,
          endTime,
          sessionDuration: duration,
          notes,
          selectedAddress: state?.selectedAddress,
          requiredCredits,
          currentCredits: userCredits,
        },
      });
    }
  };

  const bookSessionDirectly = async () => {
    if (!user?.id || !trainer?._id) {
      alert("Unable to book session. Please try again.");
      return;
    }

    try {
      setBooking(true);

      // Use slot ISO format if available (new format), otherwise use old format
      let bookingData: any;

      if (slotISO) {
        // New format: create slot ISO string from user's selected times
        // Get the date string (YYYY-MM-DD) - use day.fullDate if available, otherwise extract from slot
        let dateStr: string;
        if (day?.fullDate) {
          dateStr = day.fullDate;
        } else {
          // Extract date from slot ISO
          const slotTimes = parseSlotISO(slotISO);
          const originalStartDate = new Date(slotTimes.start);
          const year = originalStartDate.getUTCFullYear();
          const month = String(originalStartDate.getUTCMonth() + 1).padStart(2, '0');
          const dayOfMonth = String(originalStartDate.getUTCDate()).padStart(2, '0');
          dateStr = `${year}-${month}-${dayOfMonth}`;
        }

        // Convert user's selected times (local 12-hour format) to UTC ISO strings
        // Parse start time
        const startTime24 = formatTo24Hour(startTime);
        const [startHour, startMin] = startTime24.split(':').map(Number);

        // Parse end time
        const endTime24 = formatTo24Hour(endTime);
        const [endHour, endMin] = endTime24.split(':').map(Number);

        // Create Date objects in local timezone (the Date constructor interprets as local time)
        const startLocalDate = new Date(`${dateStr}T${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}:00`);
        const endLocalDate = new Date(`${dateStr}T${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}:00`);

        // Convert to UTC ISO strings
        // The Date constructor creates a date in local time, but toISOString() converts to UTC
        const startISO = startLocalDate.toISOString();
        const endISO = endLocalDate.toISOString();

        // Create new slot ISO string with actual booking times
        const bookingSlotISO = `${startISO}-${endISO}`;

        bookingData = {
          trainerId: trainer.isGym ? null : (trainer._id || trainer.id),
          gymId: trainer.isGym ? (trainer._id || trainer.id) : null,
          numberOfPax: state?.numberOfPax || 1,
          userId: user._id || user.id,
          slot: bookingSlotISO, // Use the booking time slot, not the original availability slot
          location: state?.selectedAddress || 'HOME',
          address: state?.address || '',
          notes: state?.notes || '',
        };
        console.log('Booking with slot ISO format:', {
          originalSlot: slotISO,
          selectedStartTime: startTime,
          selectedEndTime: endTime,
          dateStr,
          startLocalDate: startLocalDate.toString(),
          endLocalDate: endLocalDate.toString(),
          bookingSlotISO,
          bookingData
        });
      } else {
        // Old format: convert times
        const baseDate = day.fullDate; // YYYY-MM-DD format
        const startTime24 = formatTo24Hour(startTime) + ':00';
        const endTime24 = formatTo24Hour(endTime) + ':00';
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        bookingData = {
          trainerId: trainer.isGym ? null : (trainer._id || trainer.id),
          gymId: trainer.isGym ? (trainer._id || trainer.id) : null,
          numberOfPax: state?.numberOfPax || 1,
          userId: user._id || user.id,
          startDate: baseDate,
          startTime: startTime24,
          endTime: endTime24,
          timezone: userTimezone,
          location: state?.selectedAddress || 'HOME',
          address: state?.address || '',
          notes: state?.notes || '',
        };
        console.log('Booking with old format:', bookingData);
      }

      console.log('Booking session with data:', bookingData);
      const result = await bookSession(bookingData);

      // Navigate to success screen or show success modal
      navigate('/student/home', {
        state: {
          bookingSuccess: true,
          bookingDetails: {
            date: day.date,
            time: `${startTime} - ${endTime}`,
            trainerName: trainer.name,
            location: state?.selectedAddress || "HOME",
            address: "Your preferred location"
          }
        }
      });

    } catch (error: any) {
      console.error('Error booking session:', error);
      alert(error.response?.data?.message || 'Failed to book session. Please try again.');
    } finally {
      setBooking(false);
    }
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-[#F7F7F7]">
      <Navbar
        background="transparent"
        spacerHeight={12}
        right={
          <CreditsBalance
            balance={loading ? 0 : userCredits}
            variant="white-only"
          />
        }
      />
      {/* Scrollable content */}
      <div
        className="flex-1 overflow-y-auto"
        style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
      >
        <div className="w-full px-5 pb-4">
          {/* header */}


          {/* title */}
          <h1 className="text-[22px] font-extrabold text-black mt-2">Session Duration</h1>

          {/* time rows */}
          <div className="mt-6 space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-[14px] font-semibold text-black">Start Time</label>
              <button
                type="button"
                onClick={() => openPicker("start")}
                className="min-w-[160px] h-[44px] border border-[#DEDEDE] bg-white px-4 grid place-items-center text-[14px] font-semibold cursor-pointer"
              >
                {startTime}
              </button>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-[14px] font-semibold text-black">End Time</label>
              <button
                type="button"
                onClick={() => openPicker("end")}
                className="min-w-[160px] h-[44px] border border-[#DEDEDE] bg-white px-4 grid place-items-center text-[14px] font-semibold cursor-pointer"
              >
                {endTime}
              </button>
            </div>
          </div>

          {/* duration bar */}
          <div className="mt-6 border border-[#DEDEDE] flex items-stretch">
            <button
              type="button"
              aria-label="decrease duration"
              onClick={dec}
              className="w-[24px] h-[24px] m-5 grid place-items-center bg-[#EB2726] text-white cursor-pointer"
            >
              <svg width="18" height="2" viewBox="0 0 18 2" fill="none">
                <rect width="18" height="2" fill="currentColor" />
              </svg>
            </button>
            <div className="flex-1 grid place-items-center text-[14px]">
              <span className="font-bold">{duration} mins Session</span>
            </div>
            <button
              type="button"
              aria-label="increase duration"
              onClick={inc}
              className="w-[24px] h-[24px] m-5 grid place-items-center bg-[#EB2726] text-white cursor-pointer"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M8 0h2v18H8zM0 8h18v2H0z" fill="currentColor" />
              </svg>
            </button>
          </div>

          {/* notes */}
          <div className="mt-6">
            <div className="text-[12px] text-[#8A8A8A] mb-2">Notes to Trainer</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={`“Would like to train kicks and some cardio”\n“Haven’t practised in a while”`}
              className="w-full h-[120px] border border-[#DEDEDE] bg-[#F7F7F7] rounded-none p-4 text-[14px] resize-none outline-none"
            />
          </div>

          {/* agreement */}
          <label className="mt-6 flex items-start gap-3 cursor-pointer">
            <button
              type="button"
              onClick={() => setAgreed((a) => !a)}
              className={[
                "flex-shrink-0 w-6 h-6 flex items-center justify-center border transition-colors",
                agreed ? "bg-[#D62422] border-[#D62422]" : "bg-white border-[#B1B1B1]",
              ].join(" ")}
            >
              {agreed && (
                <svg width="16" height="12" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.8402 1.85898L5.69696 12.0022L0.160156 6.46538L2.02116 4.60438L5.69696 8.28025L13.9792 -0.00195312L15.8402 1.85898Z"
                    fill="white"
                  />
                </svg>
              )}
            </button>
            <span className="text-[14px] text-[#2D2D2D] pt-0.5">
              I agree with FitQuest{" "}
              <a href="#" className="underline text-[#EB2726]">
                Terms and policies
              </a>
              . And I shall be respectful towards my trainer.
            </span>
          </label>
        </div>
      </div>

      {/* Fixed footer */}
      <div className="bg-white border-t border-[#EAEAEA]">
        <div className="mx-auto w-full px-5 py-4 space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-[16px] font-semibold text-black">Total Credits</span>
            <span className="flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M4.17657 11.0585H1L11.3812 0H13.6188L10.7647 6.21159H14L3.6188 16H1.38121L4.17657 11.0585Z"
                  fill="#FFC800"
                />
              </svg>
              <span className="text-[16px] font-extrabold text-[#202020]">
                {loading ? '...' : Math.ceil((duration / 60) * (trainer?.creditsPerHour || (trainer?.isGym ? 5 : 1)))}
              </span>
            </span>
          </div>

          <PrimaryButton
            label={booking ? "BOOKING..." : "BOOK NOW"}
            onClick={bookNow}
            className="!w-full"
            disabled={booking || loading || !agreed}
          />
        </div>
      </div>

      {/* time picker modal */}
      {showPicker && (
        <TimePicker
          onConfirm={onPickTime}
          onClose={() => setShowPicker(false)}
          initialTime={pickerType === "start" ? startTime : endTime}
          minuteStep={30}
        />
      )}
    </div>
  );
}
